import { LoaderCircle } from "lucide-react";
import { useState } from "react";
import { useAnbieterStand } from "../../api/abfragen";
import { starten, type Anbieter, type Zweck } from "../../api/anbieter";
import { fehlerText } from "../../api/client";

/** W68 (Plan 2.12): „Mit Apple fortfahren“ und „Weiter mit Google“ ueber dem Formular der Anmeldung, gleich breit und
 *  gleich hoch, Apple zuerst, darunter „oder“. Sichtbar nur, was der Server als eingerichtet meldet; ohne Antwort nichts
 *  (kein Flackern, Server vor W68). Apple nach den Human Interface Guidelines: schwarzer Knopf mit weissem Logo, im
 *  Dunkelmodus weiss mit schwarzem, das Logo so hoch wie der Knopf (die Datei bringt ihren Rand mit). Google nach den
 *  Branding Guidelines: das „G“ unveraendert in Groesse und Farbe auf seiner Flaeche. Text in der Schrift der App.
 *  W73e (S7, D-382 H2, Entscheid E12): `gesperrt` von aussen (eine Anfrage der Seite laeuft), `onLaeuft` meldet nach aussen,
 *  solange Apple oder Google startet (wahr ab dem Tipp, falsch nach einem Fehler). S8 (D-375 A7): der Satz zum Einladungscode
 *  nennt nur die eingerichteten Anbieter, Apple zuerst (wie iOS `AnbieterAnmeldung.swift`). */
export function AnbieterKnoepfe(props: { von: string; zweck?: Zweck; registrieren?: boolean; gesperrt?: boolean; onLaeuft?: (laeuft: boolean) => void }) {
  const { data: stand } = useAnbieterStand();
  const [laeuft, setLaeuft] = useState<Anbieter | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const apple = stand?.apple_web === true;
  const google = stand?.google_web === true;
  if (!apple && !google) return null;

  const gesperrt = laeuft !== null || Boolean(props.gesperrt);

  function los(anbieter: Anbieter) {
    if (gesperrt) return;
    setLaeuft(anbieter);
    props.onLaeuft?.(true);
    setFehler(null);
    // gelingt es, verlaesst die Seite den Browser-Tab; gesperrt bleibt es bis dahin
    starten(anbieter, props.zweck ?? "anmelden", props.von).catch((f: unknown) => {
      setFehler(f);
      setLaeuft(null);
      props.onLaeuft?.(false);
    });
  }

  return (
    <div className="anbieter mt-6">
      <div className="flex flex-col gap-3">
        {apple && <AppleKnopf onClick={() => los("apple")} gesperrt={gesperrt} laeuft={laeuft === "apple"} />}
        {google && <GoogleKnopf onClick={() => los("google")} gesperrt={gesperrt} laeuft={laeuft === "google"} />}
      </div>
      {fehler !== null && (
        <p role="alert" className="t-subheadline mt-3 px-4 text-pg-danger">
          {fehlerText(fehler)}
        </p>
      )}
      {props.registrieren && (
        <p className="t-footnote mt-3 px-4 text-pg-text-2">
          Auch mit {[apple && "Apple", google && "Google"].filter(Boolean).join(" oder ")} brauchst du einen Einladungscode.
        </p>
      )}
      <p className="anbieter-oder t-subheadline mt-6 flex items-center gap-3 text-pg-text-2">
        <span aria-hidden className="h-(--hairline) flex-1 bg-pg-divider" />
        oder
        <span aria-hidden className="h-(--hairline) flex-1 bg-pg-divider" />
      </p>
    </div>
  );
}

type KnopfProps = { onClick: () => void; gesperrt: boolean; laeuft: boolean };

const KNOPF =
  "anbieter-knopf t-knopf inline-flex w-full min-h-(--btn-h-gross) items-center justify-center rounded-anbieter text-center transition-opacity duration-(--pg-dur-fast) hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40";

function Lauf(props: { an: boolean }) {
  return props.an ? <LoaderCircle size={18} strokeWidth={1.75} aria-hidden className="shrink-0 animate-spin motion-reduce:animate-none" /> : null;
}

export function AppleKnopf(props: KnopfProps & { text?: string }) {
  return (
    <button type="button" onClick={props.onClick} disabled={props.gesperrt} aria-busy={props.laeuft || undefined} className={`${KNOPF} bg-pg-apple pr-4 text-pg-on-apple`}>
      {/* Logo-only-Datei: ihre Hoehe ist die Knopfhoehe, der Rand ist Teil der Datei */}
      <img src="/marken/apple-logo-weiss.svg" alt="" className="marke-hell size-(--btn-h-gross) shrink-0" />
      <img src="/marken/apple-logo-schwarz.svg" alt="" className="marke-dunkel size-(--btn-h-gross) shrink-0" />
      <span className="min-w-0 py-2">{props.text ?? "Mit Apple fortfahren"}</span>
      <Lauf an={props.laeuft} />
      {/* so breit wie das Logo: der Text steht in der Mitte des Knopfs; wird es eng (grosse Schrift), weicht dieser Platz
          zuerst, sonst liefe der Inhalt zu beiden Seiten aus dem Knopf */}
      <span aria-hidden className="w-(--btn-h-gross) min-w-0 shrink-[1000]" />
    </button>
  );
}

export function GoogleKnopf(props: KnopfProps & { text?: string }) {
  return (
    // 10 px nach den Google Branding Guidelines, `--google-abstand`: liegt neben dem 4-px-Raster (Abnahme Regel 9)
    <button
      type="button"
      data-raster-ausnahme="google-marke"
      onClick={props.onClick}
      disabled={props.gesperrt}
      aria-busy={props.laeuft || undefined}
      className={`${KNOPF} gap-(--google-abstand) border border-pg-google-rand bg-pg-google px-(--google-links) py-2 text-pg-on-google`}
    >
      <img src="/marken/google-g-hell.svg" alt="" className="google-g marke-hell" />
      <img src="/marken/google-g-dunkel.svg" alt="" className="google-g marke-dunkel" />
      <span className="min-w-0">{props.text ?? "Weiter mit Google"}</span>
      <Lauf an={props.laeuft} />
      <span aria-hidden className="w-(--google-g) min-w-0 shrink-[1000]" />
    </button>
  );
}
